import { useOrder } from '@Pacific-Web-Services/component-library'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'

const API_URL = process.env.REACT_APP_API_URL_BASE || 'http://localhost:3001'

async function parseApiError(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const body = await response.json()
    return body.message || body.error || fallback
  } catch {
    return fallback
  }
}

export default function useHandleOrderSubmit() {
  const stripe = useStripe()
  const elements = useElements()
  const { order, clearOrder } = useOrder()
  const client = useQueryClient()
  const navigate = useNavigate()
  return useMutation<void, Error, React.FormEvent<HTMLFormElement>>(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (!stripe || !elements) {
        throw new Error('Stripe failed to load. Please refresh and try again.')
      }
      // @ts-ignore
      const createUrl = API_URL + '/order/create-order-public'
      const createOrderResponse = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...order, locationId: 1 }),
      })
      if (!createOrderResponse.ok) {
        const message = await parseApiError(
          createOrderResponse,
          'Unable to create your order. Please try again.',
        )
        throw new Error(message)
      }
      const { client_secret, orderId, uuid } = await createOrderResponse.json()
      const stripeResponse = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: {
            address: {
              postal_code: order.customer.postalCode,
            },
          },
        },
      })
      if (stripeResponse.error) {
        throw new Error(stripeResponse.error.message)
      }
      if (stripeResponse.paymentIntent.status !== 'requires_capture') {
        throw new Error(
          'Payment could not be processed. You have not been charged.',
        )
      }
      const confirmUrl = API_URL + '/order/' + orderId + '/confirm-order'
      const confirmOrderReponse = await fetch(confirmUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (!confirmOrderReponse.ok) {
        const message = await parseApiError(
          confirmOrderReponse,
          'Unable to confirm your order. Your payment has not been captured. Please try again or contact us.',
        )
        throw new Error(message)
      }
      navigate('/confirm/' + uuid)
      clearOrder()
    },
    { onSuccess: () => client.invalidateQueries(['order']) },
  )
}
